import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowUpRight, ChevronLeft, ChevronRight, Calendar, Tag, X } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import { BLOG_ARTICLES } from '../data/blogArticles';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Normalisierungsfunktion: macht aus statischen Blog-Artikeln + Newsletter-Issues
// ein einheitliches Post-Format für das Karussell
function normalizeStaticPost(post) {
  return {
    key: `static-${post.id}`,
    kind: 'static',
    raw: post,
    slug: post.slug || post.id,
    title_de: post.title_de,
    title_en: post.title_en,
    summary: post.summary,
    image_url: post.image_url,
    category: post.category,
    date: post.date,
  };
}

function normalizeNewsletterIssue(issue) {
  return {
    key: `nl-${issue.id}`,
    kind: 'newsletter',
    raw: issue,
    slug: issue.slug,
    title_de: issue.headline,
    title_en: issue.headline,
    summary: issue.summary || (issue.intro || '').slice(0, 200),
    image_url: issue.image_url || null,
    category: issue.category || 'Insight',
    date: issue.published_at || issue.created_at,
  };
}

function ArticleModal({ post, onClose }) {
  const { lang } = useLanguage();
  if (!post) return null;
  const renderContent = (content) => {
    return content.split('\n\n').map((block, i) => {
      if (block.startsWith('## '))
        return <h3 key={i} className="text-[#1f1f1f] text-xl md:text-2xl font-bold uppercase tracking-tight leading-[1.3] mt-10 mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>{block.replace('## ', '')}</h3>;
      if (block.startsWith('- **')) {
        const items = block.split('\n').filter(l => l.startsWith('- '));
        return (
          <ul key={i} className="space-y-3 my-4">
            {items.map((item, j) => (
              <li key={j} className="flex items-start gap-3 text-[#1f1f1f]/70 text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#befa0a] mt-2.5 flex-shrink-0" />
                <span dangerouslySetInnerHTML={{ __html: item.replace(/^- /, '').replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#1f1f1f]">$1</strong>') }} />
              </li>
            ))}
          </ul>
        );
      }
      return <p key={i} className="text-[#1f1f1f]/70 text-base leading-relaxed mb-4" style={{ fontFamily: "'Manrope', sans-serif" }} dangerouslySetInnerHTML={{ __html: block.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#1f1f1f]">$1</strong>') }} />;
    });
  };

  return (
    <motion.div data-testid="article-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-white overflow-y-auto">
      <div className="max-w-[800px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <button data-testid="article-close" onClick={onClose} className="fixed top-6 right-6 z-[110] w-10 h-10 bg-[#1f1f1f] text-white flex items-center justify-center hover:bg-[#befa0a] hover:text-[#0a0a0a] transition-colors"><X size={18} /></button>
        <div className="flex items-center gap-4 mb-6">
          {post.category && <span className="text-[#befa0a] text-xs uppercase tracking-[0.2em] font-bold flex items-center gap-1.5" style={{ fontFamily: "'Oswald', sans-serif" }}><Tag size={12} /> {post.category}</span>}
          {post.date && <span className="text-[#1f1f1f]/40 text-xs flex items-center gap-1.5" style={{ fontFamily: "'Manrope', sans-serif" }}><Calendar size={12} /> {new Date(post.date).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}</span>}
        </div>
        <h1 className="text-[#1f1f1f] text-3xl md:text-4xl font-bold uppercase tracking-tight leading-[1.3] leading-[1.3] mb-8" style={{ fontFamily: "'Oswald', sans-serif" }}>{post.title_de}</h1>
        {post.image_url && <div className="aspect-[16/9] overflow-hidden mb-10"><img src={post.image_url} alt={post.title_de} width="800" height="450" loading="lazy" decoding="async" className="w-full h-full object-cover" /></div>}
        <article>{post.content ? renderContent(post.content) : <p className="text-[#1f1f1f]/70">{post.summary}</p>}</article>
        <div className="mt-16 pt-8 border-t border-[#e5e5e5]">
          <button data-testid="article-cta" onClick={() => { if (typeof onClose === 'function') onClose(); setTimeout(() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="inline-block bg-[#befa0a] text-[#0a0a0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#1f1f1f] hover:text-white transition-colors" style={{ fontFamily: "'Oswald', sans-serif" }}>{lang === 'de' ? 'Kostenlose Potenzialanalyse buchen' : 'Book free potential analysis'}</button>
        </div>
      </div>
    </motion.div>
  );
}

export default function BlogSection() {
  const { lang, t } = useLanguage();
  const [activePost, setActivePost] = useState(null);
  const [newsletterIssues, setNewsletterIssues] = useState([]);

  // Newsletter-Issues live nachladen + mit statischen Blog-Artikeln mergen
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/newsletter-issues`)
      .then((r) => { if (!cancelled && Array.isArray(r.data)) setNewsletterIssues(r.data); })
      .catch(() => {/* still — Section funktioniert weiter mit statischen Artikeln */});
    return () => { cancelled = true; };
  }, []);

  const posts = [
    ...newsletterIssues.map(normalizeNewsletterIssue),
    ...BLOG_ARTICLES.map(normalizeStaticPost),
  ].sort((a, b) => {
    const da = new Date(a.date || 0).getTime();
    const db = new Date(b.date || 0).getTime();
    return db - da;
  });

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start', slidesToScroll: 1, containScroll: 'trimSnaps' });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => { emblaApi.off('select', onSelect); emblaApi.off('reInit', onSelect); };
  }, [emblaApi, onSelect]);

  return (
    <>
      <section id="blog" data-testid="blog-section" className="bg-white py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {/* Header */}
          <div className="flex items-end justify-between mb-12">
            <div>
              <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.6 }}
                data-testid="blog-label" className="text-[#befa0a] text-xs uppercase tracking-[0.3em] mb-4 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {t('blog.label')}
              </motion.p>
              <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.7, delay: 0.1 }}
                data-testid="blog-title" className="text-[#1f1f1f] text-4xl md:text-5xl font-bold uppercase tracking-tight leading-[1.3]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {t('blog.title')}
              </motion.h2>
            </div>
            {/* Desktop arrows */}
            <div className="hidden md:flex items-center gap-2">
              <button data-testid="carousel-prev" onClick={() => emblaApi?.scrollPrev()} disabled={!canPrev}
                className={`w-11 h-11 border flex items-center justify-center transition-all ${canPrev ? 'border-[#1f1f1f] text-[#1f1f1f] hover:bg-[#1f1f1f] hover:text-white' : 'border-[#e5e5e5] text-[#e5e5e5] cursor-default'}`}>
                <ChevronLeft size={18} />
              </button>
              <button data-testid="carousel-next" onClick={() => emblaApi?.scrollNext()} disabled={!canNext}
                className={`w-11 h-11 border flex items-center justify-center transition-all ${canNext ? 'border-[#1f1f1f] text-[#1f1f1f] hover:bg-[#1f1f1f] hover:text-white' : 'border-[#e5e5e5] text-[#e5e5e5] cursor-default'}`}>
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          {/* Carousel */}
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6">
              {posts.map((post) => {
                const cardInner = (
                  <article data-testid={`blog-post-${post.slug || post.key}`}
                    className="group border border-[#e5e5e5] hover:border-[#befa0a] transition-colors duration-400 cursor-pointer h-full flex flex-col">
                    {post.image_url ? (
                      <div className="aspect-[16/10] overflow-hidden bg-[#f5f5f5]">
                        <img src={post.image_url} alt={post.title_de} width="800" height="500" loading="lazy" decoding="async" className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                      </div>
                    ) : (
                      // Fallback wenn kein Hero-Bild: dunkler Akzent-Block damit Card-Höhen konsistent bleiben.
                      // Farb-Regel: Lime nur auf Dunkel.
                      <div className="aspect-[16/10] bg-[#0a0a0a] relative overflow-hidden flex items-center justify-center">
                        <div className="absolute top-0 left-0 w-full h-1 bg-[#befa0a]" />
                        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #befa0a 0%, transparent 50%), radial-gradient(circle at 80% 70%, #befa0a 0%, transparent 50%)' }} />
                        <p className="relative text-[#befa0a] text-[10px] uppercase tracking-[0.4em] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                          ENBET Insights
                        </p>
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        {post.category && <span className="text-[#befa0a] text-[10px] uppercase tracking-[0.2em] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{post.category}</span>}
                        {post.date && <span className="text-[#1f1f1f]/30 text-[10px]" style={{ fontFamily: "'Manrope', sans-serif" }}>{new Date(post.date).toLocaleDateString('de-DE')}</span>}
                      </div>
                      <h3 className="text-[#1f1f1f] text-lg md:text-xl font-bold uppercase tracking-tight leading-[1.3] leading-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
                        {lang === 'de' ? post.title_de : (post.title_en || post.title_de)}
                      </h3>
                      <p className="text-[#1f1f1f]/60 text-sm leading-relaxed mt-3 flex-1" style={{ fontFamily: "'Manrope', sans-serif" }}>{post.summary}</p>
                      <span className="mt-5 inline-flex items-center gap-2 text-[#1f1f1f] text-xs font-bold uppercase tracking-[0.15em] group-hover:text-[#befa0a] transition-colors duration-300" style={{ fontFamily: "'Oswald', sans-serif" }}>
                        {t('blog.readMore')} <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </span>
                    </div>
                  </article>
                );
                return (
                  <div key={post.key} className="flex-[0_0_100%] md:flex-[0_0_45%] lg:flex-[0_0_38%] min-w-0">
                    {post.kind === 'newsletter' ? (
                      <Link to={`/insights/${post.slug}`} className="block h-full">
                        {cardInner}
                      </Link>
                    ) : (
                      <div onClick={() => setActivePost(post.raw)} className="h-full">
                        {cardInner}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dots */}
          {posts.length > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              {posts.map((_, i) => (
                <button key={i} data-testid={`carousel-dot-${i}`} onClick={() => emblaApi?.scrollTo(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === selectedIndex ? 'w-8 bg-[#befa0a]' : 'w-3 bg-[#e5e5e5] hover:bg-[#ccc]'}`} />
              ))}
            </div>
          )}
        </div>
      </section>

      {activePost && <ArticleModal post={activePost} onClose={() => setActivePost(null)} />}
    </>
  );
}
